import React from 'react';
import PropTypes from 'prop-types';
import { graphql, createFragmentContainer } from 'react-relay';
import { intlShape, injectIntl, FormattedMessage } from 'react-intl';
import get from 'lodash.get';
import { trackSellerDetailsView } from '../pdpTracking';
import abExperiments from '../../utils/abExperiments';
import { promiseTimeout } from '../helpers/promiseTimeout';

// components
import ComponentLoader from '../../global/ComponentLoader/ComponentLoader';
import Visibility from '../../components/global/Visibility';
import PdpRespDealerDetailsStats from '../PdpRespDealerDetailsStats/PdpRespDealerDetailsStats';
import PdpSharedDealerDetailsFormLoader from '../PdpSharedDealerDetailsForm/PdpSharedDealerDetailsFormLoader';
import PdpSharedCallDealerCta from '../PdpSharedCallDealerCta/PdpSharedCallDealerCta';
import { messages } from '../messages/pdpDealerDetailsMessages';

const feedbackImportFunc = () => {
    return import(/* webpackChunkName: "PdpRespDealerDetailsFeedback" */
    '../PdpRespDealerDetailsFeedback/PdpRespDealerDetailsFeedback').then(mod => mod.default);
};

// styles
import styles from './PdpRespDealerDetails.scss';

class PdpRespDealerDetails extends React.Component {
    constructor() {
        super();
        this.state = { loadForm: false, areSellerReviewsEnabled: false };
    }

    componentDidMount() {
        this.loadFormComponent();
        if (abExperiments.sellerReviewsPdp) {
            // MCOPOP-5539 optimize test
            promiseTimeout(1500, abExperiments.sellerReviewsPdp).then(() => {
                this.setState(() => ({ areSellerReviewsEnabled: true }));
            });
        }
    }

    loadFormComponent() {
        this.setState({
            loadForm: true,
        });
    }

    render() {
        const { item, user, intl } = this.props;
        const dealerHasPhoneNumber = !!get(item, 'seller.trackedPhone.phoneNumber');
        const inputProps = {
            hasAnimatedPlaceholder: true,
            placeholder: intl.formatMessage(messages.namePlaceholder),
        };

        const reviews = get(item, 'seller.orderFeedbackPdp') || null;
        const showOrderFeedback = this.state.areSellerReviewsEnabled;

        return (
            <div className={styles.wrapper}>
                <Visibility onContentVisible={trackSellerDetailsView} />
                <div className={styles.sectionDetails}>
                    <PdpRespDealerDetailsStats
                        seller={item.seller}
                        user={user}
                        showDescriptions
                        showOrderFeedback={showOrderFeedback}
                        reviews={reviews}
                    />
                </div>
                <div className={styles.sectionForm}>
                    <PdpSharedDealerDetailsFormLoader
                        load={this.state.loadForm}
                        item={item}
                        user={user}
                        inputProps={inputProps}
                    />

                    {dealerHasPhoneNumber && (
                        <div className={styles.contactDealer}>
                            <span className={styles.contactDealerLabel}>
                                <FormattedMessage
                                    id="PdpSharedDealerDetailsForm.call_seller"
                                    defaultMessage="You may also contact the seller by phone"
                                />
                            </span>
                            <PdpSharedCallDealerCta item={item} />
                        </div>
                    )}
                </div>
                <ComponentLoader
                    reviews={reviews}
                    seller={item.seller}
                    importFunc={feedbackImportFunc}
                    load={showOrderFeedback}
                    useLoadingComponent={false}
                />
            </div>
        );
    }
}

PdpRespDealerDetails.propTypes = {
    item: PropTypes.object.isRequired,
    user: PropTypes.object,
    intl: intlShape.isRequired,
};

export default createFragmentContainer(injectIntl(PdpRespDealerDetails), {
    user: graphql`
        fragment PdpRespDealerDetails_user on User {
            ...PdpRespDealerDetailsStats_user
            ...PdpSharedDealerDetailsForm_user
        }
    `,
    item: graphql`
        fragment PdpRespDealerDetails_item on Item {
            ...PdpSharedCallDealerCta_item
            ...PdpSharedDealerDetailsForm_item
            seller {
                serviceId
                trackedPhone {
                    phoneNumber
                }
                orderFeedbackPdp {
                    ...PdpRespDealerDetailsStats_reviews
                    ...PdpRespDealerDetailsFeedback_reviews
                }
                ...PdpRespDealerDetailsFeedback_seller
                ...PdpRespDealerDetailsStats_seller
            }
        }
    `,
});
